import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory } from "react-router-dom";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { CheckCircle2, Loader2 } from "lucide-react";
import countryData from "../../data/countrySubdivisionsCountries.json";
import subdivisionData from "../../data/countrySubdivisionsStates.json";
import {
  getCanonicalPlaceById,
  getCanonicalPlacesForCountry,
} from "../../constants/canonicalPlaces";
import { trackMetaPixel, trackStandardMetaPixel } from "../../lib/metaPixel";
import {
  captureCampaignParams,
  getStoredCampaignParams,
} from "../../lib/marketingAttribution";

const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 1024;
const JPEG_QUALITY = 0.8;
const COUNTRIES = countryData.slice().sort((a, b) => a.name.localeCompare(b.name));
const getSubdivisionOptions = (countryCode) => subdivisionData
  .filter((subdivision) => subdivision.countryCode === countryCode)
  .sort((a, b) => a.name.localeCompare(b.name));
const COUNTRY_NAME_ALIASES = {
  "united states of america": "US",
  "united states": "US",
  "usa": "US",
  "u.s.a.": "US",
};

const findCountryCode = (countryName) => {
  const cleanName = countryName?.trim().toLowerCase();
  if (!cleanName) return "";
  return COUNTRY_NAME_ALIASES[cleanName] ||
    COUNTRIES.find((country) => country.name.toLowerCase() === cleanName)?.isoCode || "";
};

const findSubdivisionCode = (countryCode, subdivisionName) => {
  if (!countryCode || !subdivisionName) return "";
  const states = getSubdivisionOptions(countryCode);
  if (states.length === 0) return "00";
  const cleanName = subdivisionName.trim().toLowerCase();
  return states.find((state) =>
    state.name.toLowerCase() === cleanName || state.isoCode.toLowerCase() === cleanName
  )?.isoCode || "";
};

const REQUIRED_FIELD_LABELS = {
  userFname: "First name",
  userLname: "Last name",
  userEmail: "Email",
  userBirthDate: "Birth date",
  countryCode: "Country",
  stateProvinceRegionCode: "State, province, or region",
  city: "City",
};

export const validateProfile = ({ formData, preceptAgreements, hasExistingProfile, imageError }) => {
  const errors = {};
  const requiredFields = hasExistingProfile
    ? ["countryCode", "stateProvinceRegionCode", "city"]
    : Object.keys(REQUIRED_FIELD_LABELS);

  requiredFields.forEach((field) => {
    if (!String(formData[field] || "").trim()) {
      errors[field] = `${REQUIRED_FIELD_LABELS[field]} is required.`;
    }
  });

  if (formData.userEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
    errors.userEmail = "Enter a valid email address.";
  }

  if (formData.userBirthDate) {
    const birthDate = new Date(`${formData.userBirthDate}T00:00:00`);
    if (Number.isNaN(birthDate.getTime()) || birthDate > new Date()) {
      errors.userBirthDate = "Enter a valid birth date that is not in the future.";
    }
  }

  const allPreceptsAccepted = ["loveGod", "loveOthers", "followYeshua"]
    .every((precept) => preceptAgreements[precept] === true);
  if (!hasExistingProfile && !allPreceptsAccepted) {
    errors.precepts = "Please affirm all three precepts to continue.";
  }

  if (imageError) errors.profilePicture = imageError;
  return errors;
};

const resizeAndCompressImage = (file) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) {
      reject(
        Object.assign(new Error("Please upload a valid image."), {
          code: "INVALID_TYPE",
        })
      );
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(imageUrl);

      const canvas = document.createElement("canvas");
      let { width, height } = image;

      const widthScale = width > 0 ? MAX_IMAGE_DIMENSION / width : 1;
      const heightScale = height > 0 ? MAX_IMAGE_DIMENSION / height : 1;
      const scale = Math.min(1, widthScale, heightScale);

      if (scale < 1) {
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("We couldn't process that image."));
        return;
      }

      context.drawImage(image, 0, 0, width, height);

      const prefersPng = file.type === "image/png";
      const outputType = prefersPng ? "image/png" : "image/jpeg";

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("We couldn't process that image."));
            return;
          }

          if (blob.size > MAX_IMAGE_SIZE_BYTES) {
            reject(
              Object.assign(new Error("Your photo is too large."), {
                code: "FILE_TOO_LARGE",
              })
            );
            return;
          }

          const baseName = file.name.replace(/\.[^/.]+$/, "");
          const extension = prefersPng ? ".png" : ".jpg";
          const processedFile = new File([blob], `${baseName}${extension}`, {
            type: outputType,
            lastModified: Date.now(),
          });

          resolve(processedFile);
        },
        outputType,
        prefersPng ? undefined : JPEG_QUALITY
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error("We couldn't process that image."));
    };

    image.src = imageUrl;
  });

export default function ProfileSetupForm({ isDarkMode }) {
  const { getAccessTokenSilently, user } = useAuth0();
  const history = useHistory();
  const [submitting, setSubmitting] = useState(false);
  const [checkingExistingProfile, setCheckingExistingProfile] = useState(true);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const submittingRef = useRef(false);

  const [formData, setFormData] = useState({
    userFname: "",
    userLname: "",
    userEmail: user?.email || "",
    userBirthDate: "",
    profilePictureUrl: "",
    addressOne: "",
    addressTwo: "",
    city: "",
    stateProvinceRegion: "",
    county: "",
    postalCode: "",
    country: "",
    countryCode: "",
    stateProvinceRegionCode: "",
    canonicalPlaceId: "",
    userBio: "",
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const [imageError, setImageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const formErrorRef = useRef(null);
  const [preceptAgreements, setPreceptAgreements] = useState({
    loveGod: false,
    loveOthers: false,
    followYeshua: false,
  });

  useEffect(() => {
    captureCampaignParams();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadExistingProfile = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
        });
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/Users/0`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 404) {
          return;
        }
        if (!response.ok) {
          throw new Error(`Profile load failed (${response.status})`);
        }

        const profile = await response.json();
        if (cancelled) return;
        setHasExistingProfile(true);
        setPreceptAgreements({ loveGod: true, loveOthers: true, followYeshua: true });
        const countryCode = findCountryCode(profile.country);
        setFormData((current) => ({
          ...current,
          userFname: profile.userFname || "",
          userLname: profile.userLname || "",
          userEmail: profile.userEmail || current.userEmail,
          userBirthDate: profile.userBirthDate?.slice(0, 10) || "",
          addressOne: profile.addressOne || "",
          addressTwo: profile.addressTwo || "",
          city: profile.city || "",
          stateProvinceRegion: profile.stateProvinceRegion || "",
          county: profile.county || "",
          postalCode: profile.postalCode || "",
          country: profile.country || "",
          countryCode,
          stateProvinceRegionCode: findSubdivisionCode(countryCode, profile.stateProvinceRegion),
          userBio: profile.userBio || "",
        }));
      } catch (error) {
        if (!cancelled) {
          setSubmitError("We couldn't load your existing profile. Please refresh and try again.");
        }
      } finally {
        if (!cancelled) setCheckingExistingProfile(false);
      }
    };

    loadExistingProfile();
    return () => { cancelled = true; };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    return () => {
      if (profileImagePreview) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [profileImagePreview]);

  const fieldContainerClass = "space-y-2";
  const sharedInputAccent =
    "transition-all duration-200 focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 placeholder:text-slate-400";
  const selectBaseClass =
    "flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50";
  const inputClass = isDarkMode
    ? `!bg-slate-800 border-slate-700 text-slate-100 ${sharedInputAccent}`
    : `!bg-slate-100 border-slate-300 text-slate-900 shadow-sm ${sharedInputAccent}`;
  const selectClass = isDarkMode
    ? `${selectBaseClass} !bg-slate-800 border-slate-700 text-slate-100 ${sharedInputAccent}`
    : `${selectBaseClass} !bg-slate-100 border-slate-300 text-slate-900 shadow-sm ${sharedInputAccent}`;
  const textAreaClass = isDarkMode
    ? `!bg-slate-800 border-slate-700 text-slate-100 ${sharedInputAccent}`
    : `!bg-slate-100 border-slate-300 text-slate-900 shadow-sm ${sharedInputAccent}`;
  const labelClass = isDarkMode
    ? "text-sm font-semibold text-slate-100"
    : "text-sm font-semibold text-slate-600";
  const sectionHeadingClass = isDarkMode
    ? "text-lg font-semibold text-slate-100"
    : "text-lg font-semibold text-slate-700";
  const cardClass = isDarkMode
    ? "bg-slate-900/95 border-slate-700/80 text-slate-100 shadow-2xl"
    : "bg-white/90 border-slate-200 shadow-2xl backdrop-blur";
  const introTextClass = isDarkMode
    ? "text-sm text-slate-300 md:text-base"
    : "text-sm text-slate-500 md:text-base";
  const helperTextClass = isDarkMode
    ? "text-xs text-slate-400"
    : "text-xs text-slate-500";
  const checkboxClass = isDarkMode
    ? "h-5 w-5 rounded border-slate-600 bg-slate-800 text-emerald-400 focus:ring-emerald-400"
    : "h-5 w-5 rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500";

  const preceptOptions = [
    {
      name: "loveGod",
      label: "I choose to love God with all of my heart, mind and soul.",
    },
    {
      name: "loveOthers",
      label: "I choose to love others as myself",
    },
    {
      name: "followYeshua",
      label: "I choose to follow Yeshua (Jesus) the Christ as King",
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (name === "countryCode") {
        const country = COUNTRIES.find((entry) => entry.isoCode === value);
        const states = value ? getSubdivisionOptions(value) : [];
        return {
          ...prev,
          countryCode: value,
          country: country?.name || "",
          stateProvinceRegionCode: states.length === 0 && value ? "00" : "",
          stateProvinceRegion: states.length === 0 ? country?.name || "" : "",
          canonicalPlaceId: "",
          city: "",
          county: "",
          postalCode: "",
        };
      }

      if (name === "stateProvinceRegionCode") {
        const state = getSubdivisionOptions(prev.countryCode)
          .find((entry) => entry.isoCode === value);
        return {
          ...prev,
          stateProvinceRegionCode: value,
          stateProvinceRegion: value === "00" ? prev.country : state?.name || "",
        };
      }

      if (name === "canonicalPlaceId") {
        const place = getCanonicalPlaceById(value);
        if (!place) {
          return {
            ...prev,
            canonicalPlaceId: "",
            city: "",
            stateProvinceRegion: "",
            county: "",
            postalCode: "",
          };
        }

        return {
          ...prev,
          canonicalPlaceId: place.id,
          country: place.country,
          countryCode: findCountryCode(place.country),
          stateProvinceRegionCode: findSubdivisionCode(findCountryCode(place.country), place.stateProvinceRegion),
          city: place.city,
          stateProvinceRegion: place.stateProvinceRegion,
          county: place.county,
          postalCode: place.postalCode,
        };
      }

      return { ...prev, [name]: value };
    });
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[name];
      if (name === "countryCode") {
        delete next.stateProvinceRegionCode;
        delete next.city;
      }
      if (name === "canonicalPlaceId") {
        delete next.countryCode;
        delete next.stateProvinceRegionCode;
        delete next.city;
      }
      return next;
    });
    setSubmitError("");
  };

  const handlePreceptChange = (event) => {
    const { name, checked } = event.target;
    setPreceptAgreements((prev) => ({ ...prev, [name]: checked }));
    setFieldErrors((current) => ({ ...current, precepts: "" }));
    setSubmitError("");
  };

  const handleImageChange = async (event) => {
    const file = event.target?.files?.[0];

    if (!file) {
      setProfileImage(null);
      setProfileImagePreview("");
      setImageError("");
      return;
    }

    setSubmitError("");
    setImageError("");

    try {
      const processedFile = await resizeAndCompressImage(file);
      setProfileImage(processedFile);
      setProfileImagePreview(URL.createObjectURL(processedFile));
      setFieldErrors((current) => ({ ...current, profilePicture: "" }));
    } catch (error) {
      setProfileImage(null);
      setProfileImagePreview("");

      if (error?.code === "FILE_TOO_LARGE") {
        setImageError(
          "Your photo is too large. Please choose an image under 2 MB."
        );
      } else if (error?.code === "INVALID_TYPE") {
        setImageError("Please upload a valid image file.");
      } else {
        setImageError("We couldn't process that image. Try a different photo.");
      }
    } finally {
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent true double-submit at the event level
    if (submittingRef.current) {
      return;
    }
    setSubmitError("");

    const validationErrors = validateProfile({
      formData,
      preceptAgreements,
      hasExistingProfile,
      imageError,
    });
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setSubmitError("Your profile is not ready yet. Complete the highlighted fields below.");
      window.requestAnimationFrame(() => {
        formErrorRef.current?.focus();
        const firstInvalidField = Object.keys(validationErrors).find((field) => field !== "precepts");
        if (firstInvalidField) document.getElementById(firstInvalidField)?.focus();
      });
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    if (profileImage && profileImage.size > MAX_IMAGE_SIZE_BYTES) {
      const message =
        "Your photo is too large. Please choose an image under 2 MB.";
      setImageError(message);
      setSubmitError(message);
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_API_AUDIENCE },
      });
      const apiUrl = process.env.REACT_APP_API_URL;

      if (!apiUrl) {
        throw new Error("API URL is not configured.");
      }

      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "canonicalPlaceId") {
          return;
        }

        if (key === "profilePictureUrl") {
          if (value) {
            payload.append("ProfilePictureUrl", value);
          }
          return;
        }

        if (value !== undefined && value !== null) {
          payload.append(key, value);
        }
      });

      payload.append("CreatedOn", new Date().toISOString());

      if (profileImage) {
        payload.append("ProfilePicture", profileImage);
      }

      const res = await fetch(`${apiUrl}/api/Users${hasExistingProfile ? "/location" : ""}`, {
        method: hasExistingProfile ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      if (!res.ok) {
        const fallbackMessage =
          "We couldn't save your profile. Please try again.";
        const errorText = await res.text();
        let errorMessage = fallbackMessage;

        if (errorText) {
          try {
            const parsed = JSON.parse(errorText);
            if (parsed?.detail && typeof parsed.detail === "string") {
              errorMessage = parsed.detail;
            } else if (parsed?.message && typeof parsed.message === "string") {
              errorMessage = parsed.message;
            } else if (parsed?.errors && typeof parsed.errors === "object") {
              errorMessage = Object.values(parsed.errors).flat().join(" ") || fallbackMessage;
            } else {
              errorMessage = fallbackMessage;
            }
          } catch {
            errorMessage = errorText;
          }
        }

        setSubmitError(errorMessage);
        if (errorMessage.toLowerCase().includes("large")) {
          setImageError(errorMessage);
        }
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }

      const attribution = getStoredCampaignParams();
      const trackingPayload = {
        origin: "GuideProfileSetup",
        ...attribution,
      };
      trackStandardMetaPixel("CompleteRegistration", trackingPayload);
      trackMetaPixel("GuideProfileCreated", trackingPayload);
      window.dispatchEvent(new Event("profile-complete"));
      history.push("/");
    } catch (err) {
      console.error("Error submitting profile:", err);
      setSubmitError(
        "Something went wrong while saving your profile. Please try again."
      );
      submittingRef.current = false;
      setSubmitting(false);
      return;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const placeOptions = useMemo(
    () => getCanonicalPlacesForCountry(formData.country),
    [formData.country]
  );
  const usesControlledPlace = placeOptions.length > 0;
  const subdivisionOptions = useMemo(
    () => formData.countryCode ? getSubdivisionOptions(formData.countryCode) : [],
    [formData.countryCode]
  );
  const locationReady = Boolean(formData.countryCode && formData.stateProvinceRegionCode);
  const requiredFields = hasExistingProfile
    ? ["countryCode", "stateProvinceRegionCode", "city"]
    : Object.keys(REQUIRED_FIELD_LABELS);
  const missingRequiredLabels = requiredFields
    .filter((field) => !String(formData[field] || "").trim())
    .map((field) => REQUIRED_FIELD_LABELS[field]);
  const missingPrecepts = !hasExistingProfile && !["loveGod", "loveOthers", "followYeshua"]
    .every((precept) => preceptAgreements[precept] === true);
  const remainingCount = missingRequiredLabels.length + (missingPrecepts ? 1 : 0);

  const requiredLabel = (label) => (
    <>{label} <span className="text-rose-400" aria-hidden="true">*</span><span className="sr-only"> required</span></>
  );
  const fieldError = (field) => fieldErrors[field] ? (
    <p id={`${field}-error`} className="text-xs font-semibold text-rose-400" role="alert">
      {fieldErrors[field]}
    </p>
  ) : null;
  const describedBy = (field) => fieldErrors[field] ? `${field}-error` : undefined;

  if (checkingExistingProfile) {
    return <div role="status" className="mx-auto max-w-3xl p-8 text-center text-white">Loading your profile...</div>;
  }

  return (
    <Card
      className={`mx-auto max-w-3xl rounded-3xl border px-8 py-10 ${cardClass}`}
    >
      <div className="mb-8 space-y-2 text-center">
        <h3 className="text-3xl font-bold tracking-tight">
          {hasExistingProfile ? "Complete your location" : "Let’s build your profile"}
        </h3>
        <p className={introTextClass}>
          {hasExistingProfile
            ? "Add your city and region so we can connect you to the right local, regional, and worldwide communities. Your street address is optional and is not used as a public group location."
            : "Share a few details so we can personalize your experience and connect you with the right geographic communities."}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-10">
        <div className={`rounded-xl border p-4 ${
          remainingCount === 0
            ? "border-emerald-500/50 bg-emerald-500/10"
            : "border-amber-400/50 bg-amber-400/10"
        }`} aria-live="polite">
          <div className="flex items-start gap-3">
            {remainingCount === 0 && <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" aria-hidden="true" />}
            <div>
              <p className="font-semibold">
                {remainingCount === 0
                  ? "Everything required is complete."
                  : `${remainingCount} required ${remainingCount === 1 ? "step remains" : "steps remain"}.`}
              </p>
              {remainingCount > 0 && (
                <p className={`mt-1 ${helperTextClass}`}>
                  Complete: {[...missingRequiredLabels, ...(missingPrecepts ? ["All three precepts"] : [])].join(", ")}.
                </p>
              )}
              <p className={`mt-1 ${helperTextClass}`}>Required fields are marked with an asterisk (*). Street address, county, postal code, photo, and bio are optional.</p>
            </div>
          </div>
        </div>
        {!hasExistingProfile && <div className="space-y-6">
          <h4 className={sectionHeadingClass}>Personal Details</h4>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className={fieldContainerClass}>
              <Label htmlFor="userFname" className={labelClass}>
                {requiredLabel("First Name")}
              </Label>
              <Input
                id="userFname"
                name="userFname"
                value={formData.userFname}
                onChange={handleChange}
                className={inputClass}
                required
                aria-invalid={Boolean(fieldErrors.userFname)}
                aria-describedby={describedBy("userFname")}
              />
              {fieldError("userFname")}
            </div>
            <div className={fieldContainerClass}>
              <Label htmlFor="userLname" className={labelClass}>
                {requiredLabel("Last Name")}
              </Label>
              <Input
                id="userLname"
                name="userLname"
                value={formData.userLname}
                onChange={handleChange}
                className={inputClass}
                required
                aria-invalid={Boolean(fieldErrors.userLname)}
                aria-describedby={describedBy("userLname")}
              />
              {fieldError("userLname")}
            </div>
            <div className={fieldContainerClass}>
              <Label htmlFor="userEmail" className={labelClass}>
                {requiredLabel("Email")}
              </Label>
              <Input
                id="userEmail"
                type="email"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleChange}
                className={inputClass}
                required
                aria-invalid={Boolean(fieldErrors.userEmail)}
                aria-describedby={describedBy("userEmail")}
              />
              {fieldError("userEmail")}
            </div>
            <div className={fieldContainerClass}>
              <Label htmlFor="userBirthDate" className={labelClass}>
                {requiredLabel("Birth Date")}
              </Label>
              <Input
                id="userBirthDate"
                type="date"
                name="userBirthDate"
                value={formData.userBirthDate}
                onChange={handleChange}
                className={inputClass}
                required
                max={new Date().toISOString().slice(0, 10)}
                aria-invalid={Boolean(fieldErrors.userBirthDate)}
                aria-describedby={describedBy("userBirthDate")}
              />
              {fieldError("userBirthDate")}
            </div>
          </div>
        </div>}

        <div className="space-y-6">
          <h4 className={sectionHeadingClass}>Profile & Address</h4>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {!hasExistingProfile && <div className={`${fieldContainerClass} md:col-span-2`}>
              <Label htmlFor="profilePicture" className={labelClass}>
                Profile Picture
              </Label>
              <Input
                id="profilePicture"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={`${inputClass} cursor-pointer file:mr-4 file:rounded-md file:border-0 file:bg-emerald-500 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-emerald-600`}
              />
              <p className={helperTextClass}>
                Upload a clear photo up to 2&nbsp;MB. We&apos;ll resize it to
                fit your profile.
              </p>
              {imageError && (
                <p className="text-xs font-medium text-rose-500 md:text-sm">
                  {imageError}
                </p>
              )}
              {profileImagePreview && (
                <div className="pt-2">
                  <img
                    src={profileImagePreview}
                    alt="Profile preview"
                    className="h-32 w-32 rounded-full object-cover shadow-md"
                  />
                </div>
              )}
            </div>}
            <div className={fieldContainerClass}>
              <Label htmlFor="addressOne" className={labelClass}>
                Address 1
              </Label>
              <Input
                id="addressOne"
                name="addressOne"
                value={formData.addressOne}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className={fieldContainerClass}>
              <Label htmlFor="addressTwo" className={labelClass}>
                Address 2
              </Label>
              <Input
                id="addressTwo"
                name="addressTwo"
                value={formData.addressTwo}
                onChange={handleChange}
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:col-span-2">
              <div className={`${fieldContainerClass} md:col-span-2`}>
                <Label htmlFor="countryCode" className={labelClass}>
                   {requiredLabel("Country")}
                </Label>
                <select
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className={selectClass}
                  autoComplete="country"
                  required
                   aria-invalid={Boolean(fieldErrors.countryCode)}
                   aria-describedby={describedBy("countryCode")}
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </option>
                  ))}
                </select>
                {fieldError("countryCode")}
              </div>
              {usesControlledPlace && (
                <div className={`${fieldContainerClass} md:col-span-2`}>
                  <Label htmlFor="canonicalPlaceId" className={labelClass}>
                    Suggested Community Location (optional)
                  </Label>
                  <select
                    id="canonicalPlaceId"
                    name="canonicalPlaceId"
                    value={formData.canonicalPlaceId}
                    onChange={handleChange}
                    className={selectClass}
                  >
                    <option value="">Enter my location below</option>
                    {placeOptions.map((place) => (
                      <option key={place.id} value={place.id}>
                        {place.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className={fieldContainerClass}>
                <Label
                  htmlFor="stateProvinceRegionCode"
                  className={labelClass}
                >
                  {requiredLabel("State/Province/Region")}
                </Label>
                <select
                  id="stateProvinceRegionCode"
                  name="stateProvinceRegionCode"
                  value={formData.stateProvinceRegionCode}
                  onChange={handleChange}
                  className={selectClass}
                  disabled={!formData.countryCode}
                  required
                  aria-invalid={Boolean(fieldErrors.stateProvinceRegionCode)}
                  aria-describedby={`stateProvinceRegionCode-help${fieldErrors.stateProvinceRegionCode ? " stateProvinceRegionCode-error" : ""}`}
                >
                  <option value="">
                    {formData.countryCode ? "Select a state, province, or region" : "Select a country first"}
                  </option>
                  {subdivisionOptions.length === 0 && formData.countryCode ? (
                    <option value="00">National / territory-wide</option>
                  ) : subdivisionOptions.map((state) => (
                    <option key={`${state.countryCode}-${state.isoCode}`} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
                <p id="stateProvinceRegionCode-help" className={helperTextClass}>
                  {formData.countryCode
                    ? "Choose an official region from this list; typed region names cannot be submitted."
                    : "Select your country first. This field will then become available."}
                </p>
                {fieldError("stateProvinceRegionCode")}
              </div>
              <div className={fieldContainerClass}>
                <Label htmlFor="city" className={labelClass}>
                  {requiredLabel("City")}
                </Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={inputClass}
                  disabled={!locationReady}
                  placeholder={locationReady ? "Enter your city" : "Select country and region first"}
                  required
                  aria-invalid={Boolean(fieldErrors.city)}
                  aria-describedby={describedBy("city")}
                />
                {fieldError("city")}
              </div>
              <div className={fieldContainerClass}>
                <Label htmlFor="county" className={labelClass}>
                  County / District
                </Label>
                <Input
                  id="county"
                  name="county"
                  value={formData.county}
                  onChange={handleChange}
                  className={inputClass}
                  disabled={!locationReady}
                />
              </div>
              <div className={fieldContainerClass}>
                <Label htmlFor="postalCode" className={labelClass}>
                  Postal Code
                </Label>
                <Input
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  className={inputClass}
                  disabled={!locationReady}
                />
              </div>
            </div>
          </div>
        </div>

        {!hasExistingProfile && <div className="space-y-2">
          <Label htmlFor="userBio" className={labelClass}>
            Bio
          </Label>
          <Textarea
            id="userBio"
            name="userBio"
            value={formData.userBio}
            onChange={handleChange}
            className={textAreaClass}
          />
          <p className={helperTextClass}>
            Share a short introduction so community members can get to know you
            better.
          </p>
        </div>}

        {!hasExistingProfile && <div className="space-y-3">
          <h4 className={sectionHeadingClass}>{requiredLabel("Precepts")}</h4>
          <p className={introTextClass}>
            Please acknowledge each precept to continue.
          </p>
          <div className="space-y-3">
            {preceptOptions.map((precept) => (
              <label
                key={precept.name}
                className="flex items-start gap-3 text-left"
              >
                <input
                  type="checkbox"
                  name={precept.name}
                  checked={preceptAgreements[precept.name]}
                  onChange={handlePreceptChange}
                  className={checkboxClass}
                />
                <span className={`text-sm md:text-base ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                  {precept.label}
                </span>
              </label>
            ))}
          </div>
          {fieldError("precepts")}
        </div>}

        {submitError && (
          <div ref={formErrorRef} tabIndex={-1} role="alert" className="rounded-lg border border-rose-500/50 bg-rose-500/10 p-3 focus:outline-none focus:ring-2 focus:ring-rose-400">
            <p className="text-sm font-semibold text-rose-400">{submitError}</p>
          </div>
        )}

        <div className="flex items-center justify-end">
          <Button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="h-12 px-8 text-base font-semibold shadow-lg shadow-emerald-300/30"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                Saving your profile…
              </span>
            ) : hasExistingProfile ? "Save location" : "Save Profile"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
